'use client';

import classNames from 'classnames';
import { ComponentProps, forwardRef, ReactNode } from 'react';
import Image from '@alltrails/core/components/Image';
import Typography from '@alltrails/core/components/Typography';
import { LinkTarget, Size } from '../../types';
import styles from './styles/styles.module.scss';

// https://www.figma.com/design/grLyoLuE5EELmXSPbDY7vb/Inspirational-content--Handoff-?node-id=1-6249&t=i4A0Lh3HfSQWsqlv-1
export type ContentCardProps = {
  className?: string;
  href?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement | HTMLDivElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  tabIndex?: number;
  target?: LinkTarget;
  size?: Size<'sm' | 'md'>;
  imageSrc?: ComponentProps<typeof Image>['src'];
  imageAlt?: string;
  imageClassName?: string;
  imageMaxWidth?: number;
  imageAspectRatio?: number;
  imageSizes?: string;
  title?: ReactNode;
  titleClassName?: string;
  titleVariant?: ComponentProps<typeof Typography>['variant'];
  titleMaxLines?: number;
  subtitle?: string;
  subtitleClassName?: string;
  extras?: ReactNode;
  extrasClassName?: string;
  bodyClassName?: string;
  children?: ReactNode;
  testId?: string;
};

const ContentCard = forwardRef<HTMLAnchorElement | HTMLButtonElement | HTMLDivElement, ContentCardProps>(
  (
    {
      className,
      href,
      onClick,
      onFocus,
      onBlur,
      onMouseEnter,
      onMouseLeave,
      tabIndex,
      target = '_self',
      size = 'md',
      imageSrc,
      imageAlt = '',
      imageClassName,
      imageMaxWidth,
      imageAspectRatio = 1.5,
      imageSizes,
      title,
      titleClassName,
      titleVariant,
      titleMaxLines,
      subtitle,
      subtitleClassName,
      extras,
      extrasClassName,
      bodyClassName,
      children,
      testId
    },
    ref
  ) => {
    const defaultTitleMaxLines = 3;
    const computedTitleMaxLines = titleMaxLines ?? defaultTitleMaxLines;

    const commonProps = {
      className: classNames(styles.container, size === 'sm' && styles.containerSmall, className),
      onClick,
      onFocus,
      onBlur,
      onMouseEnter,
      onMouseLeave,
      tabIndex,
      'data-testid': testId
    };

    const defaultTitleVariant = size === 'sm' ? 'heading100' : 'heading300';

    const content = children || (
      <>
        {imageSrc && (
          <div className={classNames(styles.imageContainer, imageClassName)} style={{ aspectRatio: imageAspectRatio }}>
            <Image
              className={styles.image}
              src={imageSrc}
              alt={imageAlt}
              width={imageMaxWidth || 0}
              height={(imageMaxWidth || 0) * (1 / imageAspectRatio)}
              style={{ transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out' }}
              sizes={imageSizes || '(max-width: 540px) 100vw, (max-width: 1120px) 50vw, 25vw'}
              layout={imageMaxWidth ? 'responsive' : undefined}
            />
          </div>
        )}
        {(title || subtitle || extras) && (
          <div className={classNames(styles.body, bodyClassName)}>
            {title && (
              <div className={classNames(styles.titleContainer, titleClassName)}>
                <div
                  className={styles.title}
                  style={{
                    WebkitLineClamp: computedTitleMaxLines,
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}
                >
                  <Typography variant={titleVariant || defaultTitleVariant}>{title}</Typography>
                </div>
                {subtitle && <div className={classNames(styles.subtitle, subtitleClassName)}>{subtitle}</div>}
              </div>
            )}
            {size !== 'sm' && <div className={classNames(styles.extras, extrasClassName)}>{extras}</div>}
          </div>
        )}
      </>
    );

    if (href) {
      return (
        <a {...commonProps} href={href} target={target} ref={ref as React.Ref<HTMLAnchorElement>}>
          {content}
        </a>
      );
    }

    if (onClick) {
      return (
        <button {...commonProps} type="button" ref={ref as React.Ref<HTMLButtonElement>}>
          {content}
        </button>
      );
    }

    return (
      <div {...commonProps} ref={ref as React.Ref<HTMLDivElement>}>
        {content}
      </div>
    );
  }
);

ContentCard.displayName = 'ContentCard';

export default ContentCard;
